/**
 * Brand OS saves that know who else edited, and the change history (cowork API):
 *   PUT /v1/brands/me?brandId=   graph + baseUpdatedAt; 409 {error:'conflict', updatedAt, by} when the graph changed since
 *   GET /v1/brands/me/history?brandId=  {entries:[{at, by:{userId, name, kind:'beatly'|'client'}, fields}]}
 * Kept beside the Brand OS page (not in coworkApi.ts) so the failed answer keeps its status and body.
 */
import { API_BASE_URL, CoworkApiError, authHeaders } from '../../api/coworkApi';
import type { BrandGraph } from '../../types/cowork';

export type EditorKind = 'beatly' | 'client';
export interface BrandHistoryEntry { at: string; by: { userId: string; name: string; kind: EditorKind }; fields: string[] }
export interface BrandConflict { updatedAt: string | null; by: string | null }

async function failed(res: Response): Promise<never> {
  const json = await res.json().catch(() => ({}));
  const body = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
  throw new CoworkApiError(typeof body.error === 'string' ? body.error : `Request failed: ${res.status}`, res.status, body);
}

const q = (brandId?: string) => (brandId ? `?brandId=${encodeURIComponent(brandId)}` : '');

/** Saves the whole graph as the person sees it; `baseUpdatedAt` is the server's lastUpdatedAt it was built on. */
export async function saveBrandGraph(graph: BrandGraph, brandId: string | undefined, baseUpdatedAt: string | null): Promise<BrandGraph> {
  const res = await fetch(`${API_BASE_URL}/v1/brands/me${q(brandId)}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(baseUpdatedAt ? { ...graph, baseUpdatedAt } : graph),
  });
  if (!res.ok) return failed(res);
  return res.json() as Promise<BrandGraph>;
}

export async function brandHistory(brandId?: string): Promise<BrandHistoryEntry[]> {
  const res = await fetch(`${API_BASE_URL}/v1/brands/me/history${q(brandId)}`, { headers: authHeaders(false) });
  if (!res.ok) return failed(res);
  const json = (await res.json()) as { entries?: BrandHistoryEntry[] };
  return Array.isArray(json.entries) ? json.entries : [];
}

/** The 409 a save got because someone else changed the Brand OS since it was loaded; null for any other error. */
export function conflictOf(err: unknown): BrandConflict | null {
  if (!(err instanceof CoworkApiError) || err.status !== 409 || err.body.error !== 'conflict') return null;
  const by = err.body.by;
  const name = typeof by === 'string' ? by : by && typeof by === 'object' && typeof (by as { name?: unknown }).name === 'string' ? (by as { name: string }).name : null;
  return { updatedAt: typeof err.body.updatedAt === 'string' ? err.body.updatedAt : null, by: name || null };
}

/** "Changed by Anna just now. Reload to see it." */
export function conflictText(c: BrandConflict): string {
  return `Changed by ${c.by || 'someone else'} just now. Reload to see it.`;
}

/** Graph keys and Brand OS sections in words, for the history list. Unknown keys are shown as they come. */
const FIELD_LABELS: Record<string, string> = {
  brand: 'Brand Id', identity: 'Identity', voice: 'Voice', content: 'Content style', promotables: 'Promotables',
  audience: 'Audiences', audiences: 'Audiences', competitor: 'Competitors', competitors: 'Competitors', logos: 'Logos',
  primaryColors: 'Colours', fonts: 'Fonts', website: 'Website', name: 'Name', description: 'Description',
  voiceTone: 'Tone of voice', keyMessages: 'Key messages', thingsToAvoid: 'Things to avoid', library: 'Library',
};

export function fieldLabel(field: string): string {
  const last = field.split('.').pop() || field;
  return FIELD_LABELS[field] || FIELD_LABELS[last] || last.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

export function editorLabel(kind: EditorKind): string {
  return kind === 'beatly' ? 'Beatly' : 'Your team';
}
